import React, { useEffect, useState } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Area,
    AreaChart,
    CartesianGrid,
} from "recharts";
import {
    ShoppingBag,
    Wallet,
    Clock,
    MapPin,
    PackageCheck,
} from "lucide-react";
import { api } from "@/lib/api";

export default function AdminAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/analytics")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div
                data-testid="analytics-loading"
                className="text-sm text-forest-700/70"
            >
                Loading analytics…
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-sm text-rose-600">
                Could not load analytics.
            </div>
        );
    }

    const stats = [
        {
            label: "Total Orders",
            value: data.total_orders.toLocaleString(),
            icon: ShoppingBag,
            testid: "stat-total-orders",
        },
        {
            label: "Total Revenue",
            value: `${data.total_revenue.toLocaleString()} PKR`,
            icon: Wallet,
            testid: "stat-total-revenue",
        },
        {
            label: "Pending Orders",
            value: data.pending_orders.toLocaleString(),
            icon: Clock,
            testid: "stat-pending-orders",
        },
        {
            label: "Products Sold (top)",
            value: data.top_products?.[0]?.name || "—",
            icon: PackageCheck,
            testid: "stat-top-product",
            small: true,
        },
    ];

    return (
        <div data-testid="admin-analytics" className="space-y-8">
            <header>
                <h1 className="font-serif text-3xl sm:text-4xl font-bold text-forest-800">
                    Store overview
                </h1>
                <p className="text-sm text-forest-900/70 mt-1">
                    A live snapshot of your Resham Drops storefront.
                </p>
            </header>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map(({ label, value, icon: Icon, testid, small }) => (
                    <div
                        key={label}
                        data-testid={testid}
                        className="bg-white rounded-2xl border border-forest-100 p-5 shadow-sm"
                    >
                        <div className="flex items-center gap-2 text-forest-700/70 text-xs font-bold uppercase tracking-widest mb-3">
                            <Icon className="w-4 h-4 text-gold-600" />
                            {label}
                        </div>
                        <div
                            className={`font-serif font-bold text-forest-800 ${
                                small ? "text-base sm:text-xl" : "text-2xl sm:text-3xl"
                            }`}
                        >
                            {value}
                        </div>
                    </div>
                ))}
            </div>

            <div className="bg-white rounded-3xl border border-forest-100 p-5 sm:p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="font-serif text-xl font-bold text-forest-800">
                            Orders last 30 days
                        </h2>
                        <p className="text-xs text-forest-900/60">
                            Order volume and revenue trend.
                        </p>
                    </div>
                </div>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={data.orders_by_day}
                            margin={{ left: 0, right: 10, top: 10, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient id="o" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#1f4a2a" stopOpacity={0.4} />
                                    <stop offset="100%" stopColor="#1f4a2a" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5dec9" />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 10, fill: "#1f4a2a" }}
                                tickFormatter={(d) => d.slice(5)}
                            />
                            <YAxis
                                tick={{ fontSize: 10, fill: "#1f4a2a" }}
                                allowDecimals={false}
                            />
                            <Tooltip
                                contentStyle={{
                                    background: "#163721",
                                    color: "#e4c267",
                                    border: "none",
                                    borderRadius: 8,
                                    fontSize: 12,
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="orders"
                                stroke="#1f4a2a"
                                strokeWidth={2}
                                fill="url(#o)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <TopList
                    testid="top-cities"
                    title="Top cities"
                    icon={MapPin}
                    rows={data.top_cities.map((c) => ({
                        label: c.city,
                        value: `${c.count} ${c.count === 1 ? "order" : "orders"}`,
                    }))}
                />
                <TopList
                    testid="top-bundles"
                    title="Top bundles"
                    icon={PackageCheck}
                    rows={data.top_bundles.map((b) => ({
                        label: b.bundle,
                        value: `${b.count} · ${b.revenue.toLocaleString()} PKR`,
                    }))}
                />
                <TopList
                    testid="top-products"
                    title="Top products"
                    icon={ShoppingBag}
                    rows={data.top_products.map((p) => ({
                        label: p.name,
                        value: `${p.count} · ${p.revenue.toLocaleString()} PKR`,
                    }))}
                />
            </div>
        </div>
    );
}

function TopList({ title, icon: Icon, rows, testid }) {
    return (
        <div
            data-testid={testid}
            className="bg-white rounded-3xl border border-forest-100 p-5 shadow-sm"
        >
            <h3 className="flex items-center gap-2 font-serif font-bold text-forest-800 text-base mb-4">
                <Icon className="w-4 h-4 text-gold-600" />
                {title}
            </h3>
            <ul className="space-y-2">
                {rows.length === 0 && (
                    <li className="text-xs text-forest-700/60">No data yet.</li>
                )}
                {rows.map((r, i) => (
                    <li
                        key={i}
                        className="flex items-center justify-between text-sm py-1.5 border-b border-forest-50 last:border-none"
                    >
                        <span className="font-semibold text-forest-800 line-clamp-1">
                            {r.label}
                        </span>
                        <span className="text-xs text-forest-700/70">
                            {r.value}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
